import styled from "styled-components";
import DirectIcon from "../_Icons/DirectIcon";
import "./iphone.scss";
import { toTime } from "helpers/dateHelper";
import { Button } from "@mui/material";
import _ from "lodash";

type Message = {
  id: string;
  text: string;
  time: string;
  fromMe?: boolean;
};

const Device = styled.div`
  position: relative;
  width: ${360 - (360 / 100) * 15}px;
  height: ${740 - (740 / 100) * 15}px;
  margin: 0 auto;
  background: #0d0d0d;
  border-radius: 42px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35), inset 0 0 0 2px #222;
  padding: 14px;
`;

const Bezel = styled.div`
  width: 100%;
  height: 100%;
  background: #0a0a0a;
  border-radius: 32px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Notch = styled.div`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: ${160 - (160 / 100) * 15}px;
  height: ${28 - (28 / 100) * 15}px;
  background: #000;
  border-radius: 20px;
  box-shadow: 0 0 0 2px #0a0a0a;
  z-index: 3;
`;

const Screen = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0f172a; /* slate-900 */
  color: #e2e8f0; /* slate-200 */
  overflow: hidden;
`;

const StatusBar = styled.div`
  height: ${44 - (44 / 100) * 15}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  font-size: 12px;
  color: #cbd5e1;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: rgba(15, 23, 42, 0.9);
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
  position: sticky;
  top: 0;
  z-index: 2;
`;

const Avatar = styled.div<{ src?: string }>`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: ${({ src }) => (src ? `url(${src}) center/cover` : "#1f2937")};
  border: 1px solid rgba(148, 163, 184, 0.25);
  flex-shrink: 0;
`;

const ChatTitle = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;

  strong {
    font-size: 14px;
    color: #e5e7eb;
  }
  span {
    font-size: 11px;
    color: #94a3b8;
  }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scrollbar-width: thin;
`;

const BubbleRow = styled.div<{ fromMe?: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  justify-content: ${({ fromMe }) => (fromMe ? "flex-end" : "flex-start")};
`;

const Bubble = styled.div<{ fromMe?: boolean }>`
  padding: 10px 12px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 1.35;
  color: ${({ fromMe }) => (fromMe ? "#0f172a" : "#e2e8f0")};
  background: ${({ fromMe }) => (fromMe ? "#93c5fd" : "rgba(30, 41, 59, 0.9)")};
  border: 1px solid
    ${({ fromMe }) => (fromMe ? "rgba(96, 165, 250, 0.65)" : "rgba(148, 163, 184, 0.18)")};
  border-bottom-right-radius: ${({ fromMe }) => (fromMe ? "4px" : "14px")};
  border-bottom-left-radius: ${({ fromMe }) => (fromMe ? "14px" : "4px")};
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
`;

const Time = styled.span`
  font-size: 10px;
  color: #94a3b8;
`;

const Composer = styled.div`
  padding: 10px 12px 14px;
  display: flex;
  gap: 8px;
  align-items: center;
  background: linear-gradient(to top, rgba(2, 6, 23, 0.85), rgba(2, 6, 23, 0));
  border-top: 1px solid rgba(148, 163, 184, 0.15);
`;

const ComposerInput = styled.input`
  flex: 1;
  background: rgba(30, 41, 59, 0.85);
  color: #e2e8f0;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 999px;
  padding: 10px 14px;
  font-size: 13px;
  outline: none;

  ::placeholder {
    color: #94a3b8;
  }
`;

const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 36px;
  border-radius: 999px;
  background: #2563eb;
  color: #fff;
  border: 0;
  cursor: pointer;
  transition: transform 0.06s ease, filter 0.15s ease;
  box-shadow: 0 8px 16px rgba(37, 99, 235, 0.35);

  &:hover {
    filter: brightness(1.05);
  }
  &:active {
    transform: translateY(1px);
  }
`;

interface AutomationProps {
  account: any;
  automation: any;
}

function IPhoneChat({ account, automation }: AutomationProps) {
  function buildTriggerMessage() {
    let triggerMessage = "Любое сообщение";
    if (automation?.triggerType === "KEYWORD") {
      triggerMessage = _.get(automation, "keywords[0]");
    }

    return (
      <div className="mb-2">
        <BubbleRow fromMe={true} className="mb-2">
          <div>
            <Bubble fromMe={true} style={{ background: "#2563eb", color: "#fff" }}>
              <div>{triggerMessage}</div>
            </Bubble>
          </div>
        </BubbleRow>
      </div>
    );
  }
  return (
    <Device>
      <Bezel>
        <Notch />
        <Screen>
          <StatusBar>
            <span>{toTime(new Date())}</span>
            <span style={{ fontSize: "10px" }}>LTE • 100%</span>
          </StatusBar>
          <ChatHeader>
            <Avatar src={account?.externalAccountPhoto} />
            <ChatTitle>
              <strong>{account?.externalAccountUserName}</strong>
              <span>Онлайн</span>
            </ChatTitle>
          </ChatHeader>
          <Messages>
            {/* Trigger keywords or any text */}
            {buildTriggerMessage()}
            {automation?.nodes?.map((node: any, index: number) => (
              <div key={index} className="">
                <BubbleRow fromMe={node.fromMe} className="mb-2">
                  {!node.fromMe && <Avatar />}
                  <div>
                    <Bubble fromMe={node.fromMe}>
                      <div>{node.text}</div>
                      {/* Buttons  */}
                      <div className="mt-4">
                        {node.buttons?.map((b: any, buttonIndex: number) => (
                          <div
                            key={buttonIndex}
                            className="text-xs text-center mb-2"
                            style={{ border: "1px solid #e2e8f0", padding: 4, borderRadius: 4 }}
                          >
                            {b.text}
                          </div>
                        ))}
                      </div>
                    </Bubble>
                    <div style={{ marginTop: 4, textAlign: node.fromMe ? "right" : "left" }}>
                      <Time>{node.time}</Time>
                    </div>
                  </div>
                </BubbleRow>
                <BubbleRow fromMe={!node.fromMe} className="mb-2">
                  <div>
                    <Bubble
                      fromMe={node.fromMe}
                      style={{
                        background: "#2563eb",
                      }}
                    >
                      <div>{node.buttons?.[0]?.text}</div>
                    </Bubble>
                  </div>
                </BubbleRow>
              </div>
            ))}
          </Messages>
          <Composer>
            <ComposerInput placeholder="Напишите сообщение…" />
            <SendButton title="Отправить">
              <DirectIcon size={18} />
            </SendButton>
          </Composer>
        </Screen>
      </Bezel>
    </Device>
  );
}

export default IPhoneChat;
